import { NgClass } from '@angular/common'
import { AfterViewInit, Component, DestroyRef, ElementRef, Input, OnInit, ViewChild, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'

/**
 * TextAreaComponent - A component for rendering a text area with various features such as character limit, placeholder, label, and icon.
 */
@Component({
  selector: 'text-area',
  templateUrl: './text-area.component.html',
  styleUrls: ['./text-area.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgClass]
})
export class TextAreaComponent implements AfterViewInit, OnInit {
  /**
   * @property control - The FormControl for the textarea
   */
  @Input() control!: FormControl
  /**
   * @property id - The id for the textarea
   */
  @Input() id: string = 'textarea-input'
  /**
   * @property placeholder - The placeholder text for the textarea
   */
  @Input() placeholder: string = ''
  /**
   * @property label - The label for the textarea
   */
  @Input() label: string = ''
  /**
   * @property counter - Whether or not to display a character counter
   */
  @Input() counter: boolean = false
  /**
   * @property icon - The SVG icon to display in the textarea
   */
  @Input() icon: string = ''
  /**
   * @property iconType - The type of icon to display in the textarea
   */
  @Input() iconType: string = 'fill'
  /**
   * @property Setter for maxCharacters, also updates FormControl validators when set.
   */
  @Input()
  set maxCharacters(max: number) {
    this._maxCharacters = max
    if (this._validatorsInitialized) this._applyMaxLengthValidator()
  }
  /**
   * @property Getter for maxCharacters property
   */
  get maxCharacters(): number {
    return this._maxCharacters
  }
  /**
   * @property _maxCharacters - The maximum number of characters allowed in the textarea
   */
  private _maxCharacters: number = 256
  private _existingValidators: ValidatorFn[] = []
  private _validatorsInitialized = false
  /**
   * @property currentCharacters - The current number of characters in the textarea
   */
  public currentCharacters: number = 0
  /**
   * @property rows - The number of rows to display in the textarea
   */
  public rows: number = 1
  public isFocused: boolean = false
  /**
   * @property Reference to the textarea element
   */
  @ViewChild('textarea', { static: false }) textarea!: ElementRef
  private readonly _destroyRef = inject(DestroyRef)

  /**
   * @constructor Creates an instance of TextAreaComponent.
   * @param _Sanitizer DomSanitizer
   */
  constructor(private _Sanitizer: DomSanitizer) {}

  /**
   * Sets up a subscription to the valueChanges observable of the FormControl.
   * @returns void
   */
  ngOnInit(): void {
    this._existingValidators = this.control.validator ? [this.control.validator] : []
    this._applyMaxLengthValidator()
    this._validatorsInitialized = true
    this.currentCharacters = this.control.value?.length ?? 0

    this.control.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value) => {
      if (value && value.length > this.maxCharacters) {
        // if the new value exceeds the maximum, trim it down and prevent an infinite loop
        this.control.setValue(value.substring(0, this.maxCharacters), {
          emitEvent: false
        })
        // set currentCharacters to maxCharacters as we have reached the limit
        this.currentCharacters = this.maxCharacters
      } else {
        // update character count
        this.currentCharacters = value ? value.length : 0
      }

      // adjust textarea height
      this._adjustHeight()
    })
  }

  /**
   * After the view has been initialized, adjust the height of the textarea to fit the content.
   * This is done after the view has been initialized to ensure that the textarea has been rendered.
   * @returns void
   */
  ngAfterViewInit(): void {
    this._adjustHeight()
  }

  /**
   * @method Sanitize SVG icon to be rendered in template
   * @param icon - The SVG icon string to sanitize
   * @returns - The sanitized HTML string
   */
  public sanitizeSVG(icon: string): SafeHtml {
    return this._Sanitizer.bypassSecurityTrustHtml(icon)
  }

  public onFocus(): void {
    this.isFocused = true
  }

  public onBlur(): void {
    this.isFocused = false
  }

  /**
   * @method Adjusts the height of the textarea to fit the content
   * @returns void
   */
  private _applyMaxLengthValidator(): void {
    if (!this.control) return

    this.control.setValidators([...this._existingValidators, Validators.maxLength(this._maxCharacters)])
    this.control.updateValueAndValidity({ emitEvent: false })
  }

  private _adjustHeight(): void {
    const textarea = this.textarea?.nativeElement as HTMLTextAreaElement | undefined
    if (!textarea) return

    const scrollParent = this._findScrollParent(textarea)
    const savedScrollTop = scrollParent?.scrollTop ?? 0

    textarea.style.overflow = 'hidden'
    textarea.style.height = '0px'
    textarea.style.height = `${textarea.scrollHeight}px`

    if (scrollParent) {
      scrollParent.scrollTop = savedScrollTop
    }

    this.currentCharacters = this.control?.value?.length ?? 0
  }

  /**
   * Finds the nearest ancestor with vertical scrolling (e.g. modal body).
   * @param element - The element to find the scroll parent of
   * @returns The nearest ancestor with vertical scrolling or null if no ancestor is found
   */
  private _findScrollParent(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement

    while (parent) {
      const { overflowY } = getComputedStyle(parent)
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        return parent
      }
      parent = parent.parentElement
    }

    return null
  }

  /**
   * @method Prevents pasting text that would exceed the maximum character limit
   * @param event - The clipboard event
   * @returns void
   */
  public preventExceedingMaxCharactersOnPaste(event: ClipboardEvent): void {
    let pastedText = event.clipboardData?.getData('text')

    if (pastedText) {
      let currentText = this.control.value ? this.control.value : ''
      let newText = currentText + pastedText

      if (newText.length > this.maxCharacters) {
        event.preventDefault()

        let trimmedNewText = newText.substring(0, this.maxCharacters)
        this.control.setValue(trimmedNewText)
      }
    }
  }
}
